// src/oura/screens/DeliveryBoard.tsx
//
// The Tracker: every client's delivery on one staff page.
//
// The headline numbers name the clients behind them, as chips that jump to
// the client. Then two lists. Clients: a row each, sorted by risk, with the
// term as a strip of months, that opens in place to show what is late, what
// waits on the client and what is next. Open work: every deliverable due to
// the end of next week, picked by day from a strip, grouped by when it is due.
// Every number comes from the clients' own Delivery tabs (../board/model.ts).
// Staff only, no business in focus.

import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, CalendarDaysIcon, ChevronDownIcon, ChevronRightIcon, MessageSquarePlusIcon, RefreshCwIcon, RowsIcon, SearchIcon } from "lucide-react";
import { Dot, PageHead, PillButton, StatusKey, Surface, SURFACE, Tag, TAG_TONE, TYPE, type Tone } from "@oura/ui";
import type { DeliveryGroup } from "@service/aeo/delivery";
import { dayLabel } from "@service/aeo/delivery";
import { plural } from "@oura/docs";
import { cn } from "../lib/cn";
import { myToday } from "../components/docs/boardModel";
import { useBoard } from "../board/useBoard";
import { useClientRequests, type BoardRequests } from "../board/requests";
import { CalendarView, DayButton, WorkRowView } from "../board/OpenWorkViews";
import { deliveryHref, WORK_COLS } from "../board/links";
import { ouraHref } from "../nav";
import {
    cardOf,
    clientsIn,
    dayStrip,
    shiftMonth,
    workOn,
    GROUP_WORD,
    HEALTH,
    openWork,
    sortCards,
    totalsOf,
    WHEN,
    WHEN_ORDER,
    type ClientCard,
    type Health,
    type MonthState,
    type WorkRow,
} from "../board/model";

/** Deliverables named in an open row's column before "and N more". */
const MAX_REFS = 5;
/** Client chips on a headline number before "+N". */
const MAX_CHIPS = 4;

const HEALTH_ORDER: Health[] = ["at_risk", "slipping", "on_track", "not_started"];

const MONTH_FILL: Record<MonthState, string> = {
    done: "bg-gray-900",
    late: "bg-red-500",
    now: "bg-blue-600",
    later: "bg-gray-200",
};
const MONTH_WORD: Record<MonthState, string> = { done: "done", late: "has work past its date", now: "under way", later: "to come" };

/** The client list's columns, from 1024px. Under that a row stacks. */
const CLIENT_COLS = "lg:grid lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1.2fr)_88px_80px_minmax(0,1fr)_92px_16px] lg:items-center lg:gap-4";

const STATUS_FILTERS: (DeliveryGroup | "all")[] = ["all", "needs_us", "waiting_client", "checking"];

/** A steady colour per person, so a manager reads the same on every row. */
const PERSON_TONES: Tone[] = ["blue", "purple", "teal", "orange", "fuchsia", "indigo"];
function personTone(name: string): Tone {
    let h = 0;
    for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return PERSON_TONES[h % PERSON_TONES.length];
}

export function DeliveryBoard() {
    const { load, reload } = useBoard();
    const today = myToday();
    const [health, setHealth] = useState<string>("all");
    const [open, setOpen] = useState<Set<string>>(new Set());
    const [query, setQuery] = useState("");
    const [manager, setManager] = useState("");
    const [day, setDay] = useState("all");
    const [client, setClient] = useState("");
    const [status, setStatus] = useState<DeliveryGroup | "all">("all");
    const [flash, setFlash] = useState<string | null>(null);
    const [view, setView] = useState<"days" | "month">("days");
    const [calMonth, setCalMonth] = useState(today.slice(0, 7));
    const [calDay, setCalDay] = useState(today);
    const { by: requests, retry: retryRequests } = useClientRequests(load.state === "ready" && !load.sample ? load.clients.map((c) => c.id) : []);

    const board = useMemo(() => {
        if (load.state !== "ready") return null;
        const cards = sortCards(load.clients.map((c) => cardOf(c, today)));
        const work = openWork(load.clients, today);
        return { cards, work, totals: totalsOf(cards, work.rows, today) };
    }, [load, today]);

    const head = (
        <PageHead
            title="Tracker"
            sentence="Where every client's delivery stands, read from each client's Delivery tab."
            status={
                board ? (
                    <>
                        {board.totals.sentence}
                        {load.state === "ready" && load.sample && <span className="block text-amber-700">Sample clients. A build reads every client's real Delivery.</span>}
                    </>
                ) : load.state === "loading" ? (
                    "Reading every client's Delivery."
                ) : null
            }
            actions={
                <span>
                    <PillButton icon={RefreshCwIcon} onClick={reload} busy={load.state === "loading"}>
                        Read again
                    </PillButton>
                </span>
            }
        />
    );

    if (load.state === "unavailable" || load.state === "error") {
        return (
            <>
                {head}
                <Surface className="px-6 py-10 text-center">
                    <p className={cn(TYPE.tt, "m-0 text-gray-900")}>{load.state === "error" ? "The Tracker could not be read" : "Delivery is not switched on here"}</p>
                    <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-500")}>
                        {load.state === "error"
                            ? "No client is shown rather than a partial picture. Press Read again, or open a client's Delivery tab."
                            : "The Tracker reads each client's Delivery tab, which this build does not serve yet."}
                    </p>
                </Surface>
            </>
        );
    }

    if (!board || load.state !== "ready") {
        return (
            <>
                {head}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={cn(SURFACE.card, "h-[168px] animate-pulse")} />
                    ))}
                </div>
            </>
        );
    }

    const { cards, work, totals } = board;
    const managerOf = new Map(cards.map((c) => [c.client.id, c.manager]));
    const managers = Array.from(new Set(cards.map((c) => c.manager).filter((m): m is string => Boolean(m)))).sort();

    // Clients
    const q = query.trim().toLowerCase();
    const shownCards = cards.filter((c) => (health === "all" || c.health === health) && (!manager || c.manager === manager) && (!q || c.client.name.toLowerCase().includes(q)));
    const offBoard = [
        ...load.noPlan.map((c) => ({ ...c, why: "Signed, with no Delivery plan in Oura yet", badge: "No plan" })),
        ...load.failed.map((c) => ({ ...c, why: "Its Delivery could not be read. Read again, or open it", badge: "Read failed" })),
    ].filter((c) => !q || c.name.toLowerCase().includes(q));
    /** Open all / Close all acts on the rows on screen, not ones a filter hides. */
    const allShownOpen = shownCards.length > 0 && shownCards.every((c) => open.has(c.client.id));
    // Off-board clients have no state or manager, so a state or manager filter hides them.
    const shownOffBoard = (health === "all" || health === "not_started") && !manager ? offBoard : [];

    // Open work
    const inFilters = work.rows.filter(
        (r) => (!client || r.client.id === client) && (status === "all" || r.item.group === status) && (!manager || managerOf.get(r.client.id) === manager),
    );
    const days = dayStrip(inFilters, today, work.until);
    const calClients = load.clients.filter((c) => (!client || c.id === client) && (!manager || managerOf.get(c.id) === manager));
    const inCalendar = (r: WorkRow) => status === "all" || r.item.group === status;
    const shownWork = inFilters.filter(
        (r) => day === "all" || (day === "overdue" ? r.when === "overdue" : day === "this_week" ? r.when === "today" || r.when === "week" : r.item.due === day),
    );

    // Headline chips
    const active = cards.filter((c) => c.health !== "not_started");
    const pct = totals.total ? Math.round((totals.done / totals.total) * 100) : 0;
    const overdueRows = work.rows.filter((r) => r.when === "overdue");
    const weekRows = work.rows.filter((r) => r.when === "today" || r.when === "week");
    const dueTodayCount = work.rows.filter((r) => r.when === "today").length;
    const behind = [...active].sort((a, b) => a.done / (a.total || 1) - b.done / (b.total || 1));

    const toggle = (id: string) =>
        setOpen((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    /** A client chip: clear what would hide the client, open its row, bring it into view. */
    const focusClient = (id: string) => {
        setHealth("all");
        setQuery("");
        setManager("");
        setOpen((s) => new Set(s).add(id));
        setFlash(id);
        window.setTimeout(() => document.getElementById(`client-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
        window.setTimeout(() => setFlash((f) => (f === id ? null : f)), 1600);
    };
    /** A headline's "Show the list": the open work list with exactly what the headline counted. */
    const goToWork = (d: string) => {
        setView("days");
        setDay(d);
        setClient("");
        setStatus("all");
        setManager("");
        document.getElementById("open-work")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const filterSelects = (
        <>
            <Select label="Status" value={status} onChange={(v) => setStatus(v as DeliveryGroup | "all")}>
                {STATUS_FILTERS.map((s) => (
                    <option key={s} value={s}>
                        {s === "all" ? "Every status" : GROUP_WORD[s].label}
                    </option>
                ))}
            </Select>
            <Select label="Client" value={client} onChange={setClient}>
                <option value="">All clients</option>
                {active.map((c) => (
                    <option key={c.client.id} value={c.client.id}>
                        {c.client.name}
                    </option>
                ))}
            </Select>
            {managers.length > 0 && (
                <Select label="Account manager" value={manager} onChange={setManager}>
                    <option value="">Everyone's work</option>
                    {managers.map((m) => (
                        <option key={m} value={m}>
                            {m}'s clients
                        </option>
                    ))}
                </Select>
            )}
        </>
    );

    return (
        <>
            {head}
            <div className="space-y-8">
                {/* The headline, each number with the clients behind it */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Metric
                        label="Work done"
                        value={totals.done}
                        of={totals.total}
                        sub={`${pct}% of the term across ${plural(totals.active, "active client")}. Least done first:`}
                        chips={behind.map((c) => ({ id: c.client.id, name: c.client.name, tone: HEALTH[c.health].tone, count: `${c.total ? Math.round((c.done / c.total) * 100) : 0}%` }))}
                        onChip={focusClient}
                    />
                    <Metric
                        label="At risk"
                        value={totals.atRisk.length}
                        of={totals.active}
                        sub={`${cards.filter((c) => c.health === "slipping").length} more slipping`}
                        chips={cards
                            .filter((c) => c.health === "at_risk" || c.health === "slipping")
                            .map((c) => ({ id: c.client.id, name: c.client.name, tone: HEALTH[c.health].tone, count: c.health === "at_risk" ? "At risk" : "Slipping" }))}
                        empty={totals.active === 0 ? "No client has started delivery yet." : "Every active client is on track."}
                        onChip={focusClient}
                        onOpen={() => {
                            setHealth("at_risk");
                            document.getElementById("clients")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                        openLabel="Show at risk"
                    />
                    <Metric
                        label="Past its date"
                        value={totals.late}
                        tone={totals.late > 0 ? "red" : undefined}
                        sub={totals.lateOnClient > 0 ? `${totals.lateOnClient} of them waiting on the client` : "None waiting on the client"}
                        chips={clientsIn(overdueRows).map((e) => ({ id: e.client.id, name: e.client.name, tone: "red", count: String(e.count) }))}
                        empty="Nothing is past its date."
                        onChip={focusClient}
                        onOpen={() => goToWork("overdue")}
                        openLabel="Show the list"
                    />
                    <Metric
                        label="Due this week"
                        value={weekRows.length}
                        sub={`${dueTodayCount} today, the rest by ${dayLabel(totals.weekEnds)}`}
                        chips={clientsIn(weekRows).map((e) => ({ id: e.client.id, name: e.client.name, tone: "blue", count: String(e.count) }))}
                        empty="Nothing else is due this week."
                        onChip={focusClient}
                        onOpen={() => goToWork("this_week")}
                        openLabel="Show the list"
                    />
                </div>

                {/* Clients */}
                <section id="clients" className="scroll-mt-4">
                    <SectionTitle title="Clients" sentence="Sorted by risk. The strip is the term, a block a month. Open a client to see what is late, what waits on them and what is next." />
                    <Surface flush>
                        <div className="flex flex-col gap-3 border-b border-gray-100 p-4 xl:flex-row xl:items-center">
                            <StatusKey
                                ariaLabel="Filter by state"
                                groups={HEALTH_ORDER.map((h) => ({ key: h, label: HEALTH[h].label, tone: HEALTH[h].tone, count: cards.filter((c) => c.health === h).length }))}
                                value={health}
                                onChange={setHealth}
                                className="flex-1"
                            />
                            <div className="flex flex-wrap items-center gap-2">
                                <label className="relative">
                                    <span className="sr-only">Find a client</span>
                                    <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden />
                                    <input
                                        id="board-client-search"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Find a client"
                                        className={cn(TYPE.tc, "h-[30px] w-44 rounded-full bg-white pl-8 pr-3 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600")}
                                    />
                                </label>
                                {managers.length > 0 && (
                                    <Select label="Account manager" value={manager} onChange={setManager}>
                                        <option value="">Everyone's clients</option>
                                        {managers.map((m) => (
                                            <option key={m} value={m}>
                                                {m}'s clients
                                            </option>
                                        ))}
                                    </Select>
                                )}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setOpen((s) => {
                                            const n = new Set(s);
                                            for (const c of shownCards) {
                                                if (allShownOpen) n.delete(c.client.id);
                                                else n.add(c.client.id);
                                            }
                                            return n;
                                        })
                                    }
                                    className={cn(TYPE.tc, "px-1 font-medium text-blue-700 hover:underline")}
                                >
                                    {allShownOpen ? "Close all" : "Open all"}
                                </button>
                            </div>
                        </div>

                        <div className={cn(CLIENT_COLS, "hidden border-b border-gray-100 px-4 py-2.5", TYPE.tc, "text-gray-500")}>
                            <span>Client</span>
                            <span>Term</span>
                            <span>Past its date</span>
                            <span>On client</span>
                            <span>Next date</span>
                            <span>State</span>
                            <span />
                        </div>

                        {shownCards.length === 0 && shownOffBoard.length === 0 ? (
                            <p className={cn(TYPE.tb, "m-0 py-8 text-center text-gray-500")}>No client matches.</p>
                        ) : (
                            <ul className="list-plain m-0 divide-y divide-gray-100 p-0">
                                {shownCards.map((c) => (
                                    <ClientRow key={c.client.id} card={c} requests={requests.get(c.client.id)} onRetryRequests={() => retryRequests(c.client.id)} open={open.has(c.client.id)} flash={flash === c.client.id} onToggle={() => toggle(c.client.id)} />
                                ))}
                                {shownOffBoard.map((c) => (
                                    <li key={c.id}>
                                        <Link to={deliveryHref(c.id)} className={cn(CLIENT_COLS, "flex items-center gap-3 px-4 py-3 no-underline hover:bg-gray-50")}>
                                            <span className="flex min-w-0 flex-1 items-center gap-3 lg:col-span-5">
                                                <Avatar name={null} />
                                                <span className="min-w-0">
                                                    <span className={cn(TYPE.tb, "block font-medium text-gray-400")}>{c.name}</span>
                                                    <span className={cn(TYPE.tc, "block text-gray-400")}>{c.why}</span>
                                                </span>
                                            </span>
                                            <span>
                                                <Tag tone="gray">{c.badge}</Tag>
                                            </span>
                                            <ChevronRightIcon className="hidden h-4 w-4 text-gray-300 lg:block" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Surface>
                </section>

                {/* Open work: by day, or the month as a calendar */}
                <section id="open-work" className="scroll-mt-4">
                    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                        <SectionTitle
                            flush
                            title={view === "days" ? `Open work to ${dayLabel(work.until)}` : "Calendar"}
                            sentence={
                                view === "days"
                                    ? "Every dated deliverable not yet done. Pick a day, or past its date. Open one to work on it."
                                    : "Every deliverable on the day it is due, done or not. Pick a day to see its work."
                            }
                        />
                        <div role="group" aria-label="View" className="inline-flex rounded-full bg-white p-0.5 ring-1 ring-inset ring-gray-200">
                            {(
                                [
                                    ["days", "Days", RowsIcon],
                                    ["month", "Month", CalendarDaysIcon],
                                ] as const
                            ).map(([k, label, Icon]) => (
                                <button
                                    key={k}
                                    type="button"
                                    aria-pressed={view === k}
                                    onClick={() => setView(k)}
                                    className={cn(TYPE.tc, "inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-medium", view === k ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-50")}
                                >
                                    <Icon className="h-3.5 w-3.5" aria-hidden />
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Surface flush>
                        {view === "days" ? (
                            <>
                                <div className="border-b border-gray-100 p-4">
                                    <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
                                <DayButton active={day === "all"} onClick={() => setDay("all")} top="All" big={String(inFilters.length)} bottom="open" />
                                <DayButton
                                    active={day === "this_week"}
                                    onClick={() => setDay(day === "this_week" ? "all" : "this_week")}
                                    top="This"
                                    big={String(inFilters.filter((r) => r.when === "today" || r.when === "week").length)}
                                    bottom="week"
                                />
                                {days.map((d) =>
                                    d.key === "overdue" ? (
                                        <DayButton
                                            key="overdue"
                                            active={day === "overdue"}
                                            onClick={() => setDay(day === "overdue" ? "all" : "overdue")}
                                            top="Past"
                                            big={String(d.count)}
                                            bottom="its date"
                                            alert={d.count > 0}
                                        />
                                    ) : (
                                        <DayButton
                                            key={d.key}
                                            active={day === d.key}
                                            onClick={() => setDay(day === d.key ? "all" : d.key)}
                                            top={d.key === today ? "Today" : dayLabel(d.key).split(" ")[0]}
                                            big={d.count > 0 ? String(d.count) : "–"}
                                            bottom={dayLabel(d.key).split(" ").slice(1).join(" ")}
                                            muted={d.count === 0}
                                            today={d.key === today}
                                        />
                                    ),
                                )}
                            </div>
                                    <div className="mt-3 flex flex-wrap gap-2">{filterSelects}</div>
                                </div>
                                {shownWork.length === 0 ? (
                            <p className={cn(TYPE.tb, "m-0 py-8 text-center text-gray-500")}>Nothing due here.</p>
                        ) : (
                            <>
                                <div className={cn(WORK_COLS, "hidden border-b border-gray-100 px-4 py-2.5", TYPE.tc, "text-gray-500")}>
                                    <span>Date</span>
                                    <span>Client</span>
                                    <span>Deliverable</span>
                                    <span>Owner</span>
                                    <span>Status</span>
                                </div>
                                {WHEN_ORDER.filter((w) => shownWork.some((r) => r.when === w)).map((w) => {
                                    const rows = shownWork.filter((r) => r.when === w);
                                    return (
                                        <div key={w}>
                                            <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/70 px-4 py-2">
                                                <Dot tone={WHEN[w].tone} />
                                                <span className={cn(TYPE.tc, "font-semibold text-gray-900")}>{WHEN[w].label}</span>
                                                <span className={cn(TYPE.tc, "tabular-nums text-gray-400")}>{rows.length}</span>
                                            </div>
                                            <ul className="list-plain m-0 divide-y divide-gray-100 p-0">
                                                {rows.map((r) => (
                                                    <WorkRowView key={`${r.client.id}:${r.item.key}`} row={r} />
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                            </>
                        ) : (
                            <CalendarView
                                month={calMonth}
                                today={today}
                                day={calDay}
                                rowsOn={(d) => workOn(calClients, d, today).filter(inCalendar)}
                                onMonth={(n) => {
                                    // The picked day moves with the month, so the list never shows a day off the calendar.
                                    const m = shiftMonth(calMonth, n);
                                    setCalMonth(m);
                                    setCalDay(m === today.slice(0, 7) ? today : `${m}-01`);
                                }}
                                onToday={() => {
                                    setCalMonth(today.slice(0, 7));
                                    setCalDay(today);
                                }}
                                onDay={(d) => {
                                    setCalDay(d);
                                    if (d.slice(0, 7) !== calMonth) setCalMonth(d.slice(0, 7));
                                }}
                                filters={filterSelects}
                            />
                        )}
                    </Surface>
                </section>

                <p className={cn(TYPE.tc, "m-0 max-w-3xl text-gray-400")}>
                    On track: nothing past its date. Slipping: 1 or 2 of ours past their date. At risk: 3 or more past their date, or one past its date waiting on the
                    client. Read {dayLabel(load.readAt.slice(0, 10))}.
                </p>
            </div>
        </>
    );
}

/* ------------------------------------------------------------------------- *
 * Parts
 * ------------------------------------------------------------------------- */

function SectionTitle({ title, sentence, flush }: { title: ReactNode; sentence: ReactNode; flush?: boolean }) {
    return (
        <div className={flush ? undefined : "mb-3"}>
            <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>{title}</h2>
            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{sentence}</p>
        </div>
    );
}

function Select({ label, value, onChange, children }: { label: string; value: string; onChange: (v: string) => void; children: ReactNode }) {
    return (
        <select
            aria-label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={cn(TYPE.tc, "h-[30px] rounded-full bg-white px-3 font-medium text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600")}
        >
            {children}
        </select>
    );
}

interface MetricChip {
    id: string;
    name: string;
    tone: Tone;
    count: string;
}

/** A headline number with the clients behind it as chips. Each chip jumps to its client. */
function Metric({
    label,
    value,
    of,
    tone,
    sub,
    chips,
    empty,
    onChip,
    onOpen,
    openLabel,
}: {
    label: string;
    value: number;
    of?: number;
    tone?: "red";
    sub: string;
    chips: MetricChip[];
    empty?: string;
    onChip: (id: string) => void;
    onOpen?: () => void;
    openLabel?: string;
}) {
    const more = chips.length - MAX_CHIPS;
    return (
        <section className={cn(SURFACE.card, "flex min-w-0 flex-col p-4")}>
            <div className="flex items-baseline justify-between gap-2">
                <span className={TYPE.lab}>{label}</span>
                {onOpen && (
                    <button type="button" onClick={onOpen} className={cn(TYPE.tm, "font-medium text-blue-700 hover:underline")}>
                        {openLabel}
                    </button>
                )}
            </div>
            <span className={cn(TYPE.num, "mt-1 block", tone === "red" ? "text-red-600" : "text-gray-900")}>
                {value}
                {of !== undefined && <span className="text-[17px] font-medium leading-6 tracking-normal text-gray-400"> of {of}</span>}
            </span>
            <span className={cn(TYPE.tc, "mt-1 text-gray-500")}>{sub}</span>
            <div className="mt-3 flex flex-wrap gap-1.5">
                {chips.length === 0 && empty && <span className={cn(TYPE.tc, "text-gray-400")}>{empty}</span>}
                {chips.slice(0, MAX_CHIPS).map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        onClick={() => onChip(c.id)}
                        title={`Show ${c.name}`}
                        className={cn(
                            "inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset transition hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600",
                            TAG_TONE[c.tone],
                        )}
                    >
                        <span className="truncate">{c.name}</span>
                        <span className="shrink-0 tabular-nums opacity-70">{c.count}</span>
                    </button>
                ))}
                {more > 0 && <span className={cn(TYPE.tm, "self-center text-gray-500")}>+{more}</span>}
            </div>
        </section>
    );
}

function Avatar({ name }: { name: string | null }) {
    if (!name) return <span aria-hidden className="h-8 w-8 shrink-0 rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200" />;
    return (
        <span title={`Account manager: ${name}`} className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ring-1 ring-inset", TAG_TONE[personTone(name)])}>
            {name
                .split(/\s+/)
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
        </span>
    );
}

/** The term, a block a month: done, past its date, under way, to come. */
function MonthStrip({ card: c }: { card: ClientCard }) {
    return (
        <span className="flex min-w-0 flex-1 items-center gap-2">
            <span className="flex min-w-[96px] flex-1 gap-[3px]" role="img" aria-label={`${c.done} of ${c.total} done`}>
                {c.months.map((m) => (
                    <span
                        key={m.month}
                        title={`Month ${m.month}: ${MONTH_WORD[m.state]}, ${m.done} of ${m.total} done${m.due ? `, due ${dayLabel(m.due)}` : ""}`}
                        className={cn("h-2 flex-1 rounded-[3px]", MONTH_FILL[m.state], m.state === "now" && "ring-2 ring-blue-200")}
                    />
                ))}
            </span>
            <span className={cn(TYPE.tc, "whitespace-nowrap tabular-nums text-gray-700")}>
                {c.done}
                <span className="text-gray-400"> of {c.total}</span>
            </span>
        </span>
    );
}

function ClientRow({
    card: c,
    requests,
    onRetryRequests,
    open,
    flash,
    onToggle,
}: {
    card: ClientCard;
    requests?: BoardRequests;
    onRetryRequests: () => void;
    open: boolean;
    flash: boolean;
    onToggle: () => void;
}) {
    const fresh = requests?.state === "ready" ? requests.data.freshCount : 0;
    const pct = c.total ? Math.round((c.done / c.total) * 100) : 0;
    const h = HEALTH[c.health];
    const started = c.health !== "not_started";
    return (
        <li id={`client-${c.client.id}`} className={cn("scroll-mt-24 transition-colors duration-700", flash && "bg-blue-50")}>
            <button
                type="button"
                aria-expanded={open}
                onClick={onToggle}
                className={cn(
                    CLIENT_COLS,
                    "group flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600",
                    open && !flash && "bg-gray-50",
                )}
            >
                <span className="flex min-w-0 flex-1 basis-[60%] items-center gap-3 lg:basis-auto">
                    <Avatar name={c.manager} />
                    <span className="min-w-0">
                        <span className="flex min-w-0 items-center gap-2">
                            <span className={cn(TYPE.tb, "truncate font-medium text-gray-900 group-hover:text-blue-700")}>{c.client.name}</span>
                            {fresh > 0 && (
                                <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset", TAG_TONE.blue)}>
                                    <MessageSquarePlusIcon className="h-3 w-3" aria-hidden />
                                    {fresh === 1 ? "New request" : `${fresh} new requests`}
                                </span>
                            )}
                            {requests?.state === "failed" && (
                                <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset", TAG_TONE.amber)}>
                                    Requests unavailable
                                </span>
                            )}
                        </span>
                        <span className={cn(TYPE.tc, "block truncate text-gray-500")}>
                            {c.client.monthLabel}
                            {c.ends ? ` · ends ${dayLabel(c.ends)}` : ""}
                        </span>
                    </span>
                </span>

                <span className="order-last flex w-full lg:order-none lg:w-auto">
                    <MonthStrip card={c} />
                </span>

                <Fact label="Past its date" value={started ? c.late.length : "–"} alert={c.late.length > 0} />
                <Fact label="On client" value={started ? c.waiting : "–"} />
                <span className={cn(TYPE.tc, "hidden truncate text-gray-700 lg:block")}>{c.nextGate ? `${dayLabel(c.nextGate.due)} · ${c.nextGate.open.length} open` : "–"}</span>

                <span>
                    <Tag tone={h.tone}>{h.label}</Tag>
                </span>
                <ChevronDownIcon className={cn("hidden h-4 w-4 text-gray-400 transition-transform lg:block", open && "rotate-180")} aria-hidden />
            </button>

            {open && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 pb-4 pt-3">
                    {requests?.state === "failed" && (
                        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl bg-white px-3 py-2.5 ring-1 ring-amber-100">
                            <span className={cn(TYPE.tc, "min-w-0 flex-1 text-gray-700")}>Requests could not be loaded. {requests.message}</span>
                            <PillButton size="sm" icon={RefreshCwIcon} onClick={onRetryRequests}>
                                Try again
                            </PillButton>
                        </div>
                    )}
                    {requests?.state === "ready" && (
                        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl bg-white px-3 py-2.5 ring-1 ring-blue-100">
                            <Tag tone={fresh > 0 ? "blue" : "gray"}>{fresh > 0 ? (fresh === 1 ? "New request" : `${fresh} new requests`) : "Requests"}</Tag>
                            <span className={cn(TYPE.tc, "min-w-0 flex-1 truncate text-gray-700")}>
                                {fresh > 0 && requests.data.latest
                                    ? `"${requests.data.latest.title || requests.data.latest.note}", ${requestAge(requests.data.latest.at)}${fresh > 1 ? `, and ${fresh - 1} more` : ""}`
                                    : `${plural(requests.data.open, "request")} open, none new`}
                            </span>
                            <Link to={ouraHref("requests", { role: "oura-staff" })} className={cn(TYPE.tc, "inline-flex items-center gap-1 font-medium text-blue-700 no-underline")}>
                                Open requests <ArrowRightIcon className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    )}
                    <ul className="list-plain m-0 grid grid-cols-1 gap-4 p-0 md:grid-cols-3">
                        {c.notes.map((n) => (
                            <li key={n.key} className="min-w-0 rounded-xl bg-white p-3 ring-1 ring-gray-100">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Tag tone={n.chip.tone}>{n.chip.label}</Tag>
                                    <span className={cn(TYPE.tc, "text-gray-700")}>{n.text}</span>
                                </div>
                                {n.refs.length > 0 && (
                                    <ul className="m-0 mt-2 flex min-w-0 list-disc flex-col gap-1 pl-5 marker:text-gray-300">
                                        {n.refs.slice(0, MAX_REFS).map((r) => (
                                            <li key={r.key} className={cn(TYPE.tc, "min-w-0")}>
                                                <Link
                                                    to={deliveryHref(c.client.id, r.key)}
                                                    title={`${r.code} ${r.label}${r.due ? `, ${dayLabel(r.due)}` : ""}`}
                                                    className="flex min-w-0 items-baseline gap-1.5 overflow-hidden text-gray-900 no-underline hover:text-blue-700"
                                                >
                                                    <span className="min-w-0 truncate">{r.label}</span>
                                                    <span className="shrink-0 tabular-nums text-gray-400">
                                                        {r.code}
                                                        {r.month > 1 ? ` · M${r.month}` : ""}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {n.refs.length > MAX_REFS && <p className={cn(TYPE.tc, "m-0 mt-1 pl-5 text-gray-500")}>and {n.refs.length - MAX_REFS} more</p>}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                        <Link to={deliveryHref(c.client.id)} className={cn(TYPE.tc, "inline-flex items-center gap-1 font-medium text-blue-700 no-underline")}>
                            Open {c.client.name}'s Delivery <ArrowRightIcon className="h-3.5 w-3.5" />
                        </Link>
                        <span className={cn(TYPE.tm, "flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-400")}>
                            {(["done", "late", "now", "later"] as MonthState[]).map((s) => (
                                <span key={s} className="inline-flex items-center gap-1">
                                    <span className={cn("h-2 w-3 rounded-[2px]", MONTH_FILL[s])} />
                                    {s === "done" ? "Done" : s === "late" ? "Late" : s === "now" ? "Now" : "To come"}
                                </span>
                            ))}
                            <span>
                                {started && c.daysLeft !== null ? `${pct}% done · ${plural(c.daysLeft, "day")} left · ` : ""}
                                Last check {c.client.lastCheck ? dayLabel(c.client.lastCheck.slice(0, 10)) : "none yet"}
                            </span>
                        </span>
                    </div>
                </div>
            )}
        </li>
    );
}

/** "today", "yesterday" or "3 days ago", in Malaysian days. */
function requestAge(at: string): string {
    const days = Math.round((Date.parse(myToday()) - Date.parse(myToday(Date.parse(at)))) / 86_400_000);
    return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
}

/** A count in the client row: a column from 1024px, a labelled fact under the name below it. */
function Fact({ label, value, alert }: { label: string; value: ReactNode; alert?: boolean }) {
    return (
        <span className={cn(TYPE.tc, "tabular-nums", alert ? "font-medium text-red-600" : "text-gray-700")}>
            <span className="text-gray-500 lg:hidden">{label} </span>
            {value}
        </span>
    );
}
